/* eslint-disable react/prop-types */
import memoize from 'lodash/memoize';
import React from 'react';
import { generatePath, useParams, useNavigate } from 'react-router-dom';

import { Button, Modal, Select, Typography } from '@atoms';
import { LOCALES, DEFAULT_LOCALE } from '@constants';

const ACTION = {
  CHANGE: 'change',
  CANCEL: 'cancel',
};

const LocaleSelect = ({
  needsSave,
  route = ':locale',
  locale = DEFAULT_LOCALE,
  localeParamName = 'locale',
  disabled,
  loading,
  onSaveDraft,
  onPublish,
  onChange,
}) => {
  const parameters = useParams();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = React.useState();

  const changeLocale = React.useCallback(
    (_locale) => {
      if (onChange) {
        onChange(_locale);
      } else if (route) {
        navigate(generatePath(route, { ...parameters, [localeParamName]: _locale }));
      }
    },
    [onChange, navigate, route, parameters, localeParamName],
  );

  const handleChange = React.useCallback(
    (_locale) => {
      if ((typeof needsSave === 'function' && needsSave()) ?? needsSave) {
        setSelectedLocation(_locale);
      } else {
        changeLocale(_locale);
      }
    },
    [needsSave, changeLocale],
  );

  const handleSubmit = React.useMemo(
    () =>
      memoize((action) => async () => {
        if (typeof action === 'function') {
          try {
            // make sure that action (onSaveDraft, onPublish) throws an error
            await action();
            changeLocale(selectedLocation);
          } catch {
            // Error should be already handled before in action. This try just prevents from changing locale in case of an error occurred in action
          }
        } else if (action === ACTION.CHANGE) {
          changeLocale(selectedLocation);
        }
        // hide modal
        setSelectedLocation();
      }),
    [changeLocale, selectedLocation],
  );

  return (
    <>
      <Typography.Title id="language" level={4}>
        Language:
      </Typography.Title>
      <Select
        aria-labelledby="language"
        onChange={handleChange}
        value={locale}
        style={{ marginBottom: 30, width: '60%' }}
        disabled={disabled}
      >
        {LOCALES.map(([_locale, name]) => (
          <Select.Option key={_locale} value={_locale}>
            {name}
          </Select.Option>
        ))}
      </Select>
      <Modal
        visible={Boolean(selectedLocation)}
        footerDivider={false}
        title="Save changes"
        footer={
          <>
            {onPublish && (
              <Button type="primary" onClick={handleSubmit(onPublish)} loading={loading}>
                PUBLISH
              </Button>
            )}
            {onSaveDraft && (
              <Button onClick={handleSubmit(onSaveDraft)} loading={loading}>
                SAVE DRAFT
              </Button>
            )}
            <Button onClick={handleSubmit(ACTION.CHANGE)}>CHANGE</Button>
            <Button onClick={handleSubmit(ACTION.CANCEL)} style={{ float: 'right' }}>
              CANCEL
            </Button>
          </>
        }
      >
        <p>Are you sure you want to change language? Otherwise all progress won&apos;t be saved</p>
      </Modal>
    </>
  );
};

export default LocaleSelect;
